/**
 * Перешифрування вмісту з ключа пристрою на ключі записів.
 *
 * Потрібне тому, що блоб, зашифрований невитягуваним ключем пристрою
 * ([ADR-0015], алгоритм 3), не може виїхати нікуди: другий пристрій його
 * не відкриє, і синхронізувати його безглуздо. Ключі записів
 * ([ADR-0016], алгоритм 1) переносні — з них і починається M2.
 *
 * **Обліку прогресу тут немає навмисно.** Байт алгоритму в кожному блобі
 * і є позначкою, зроблений він чи ні: `seal` пише алгоритм 1, а рядки
 * з алгоритмом 3 видно запитом. Обрив посеред роботи не втрачає нічого
 * й не потребує відновлення стану — наступний прогін просто продовжить
 * з того, що лишилося. Окрема таблиця прогресу була б четвертим місцем,
 * яке може розійтися з дійсністю.
 *
 * Що тут **не** змінюється: `updated_at`, `hlc` і `device_id`. Інакше
 * весь архів виглядав би щойно зміненим, і перша ж синхронізація
 * відправила б його цілком — за той самий вміст.
 */
import { Algorithm, SCHEMA_VERSION } from "@beresta/core";
/** Скільки рядків брати за раз. Досить, щоб не ганяти запити, і мало, щоб не тримати архів у памʼяті. */
const BATCH = 100;
const TARGETS = [
    { table: "entry", column: "body" },
    { table: "horizon_item", column: "text" },
];
/**
 * Умова «цей блоб ще старий».
 *
 * Порівнюємо перші два байти — версію конверта й алгоритм. `substr` для
 * BLOB у SQLite рахує байтами, тож це саме заголовок, а не символи.
 */
function stillOld(column) {
    return `${column} IS NOT NULL
      AND length(${column}) >= 2
      AND hex(substr(${column}, 1, 2)) = ?`;
}
const OLD_HEADER = [SCHEMA_VERSION, Algorithm.AesGcm256]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
export async function countPending(db) {
    let total = 0;
    for (const { table, column } of TARGETS) {
        const rows = await db.query(`SELECT count(*) AS n FROM ${table} WHERE ${stillOld(column)}`, [OLD_HEADER]);
        total += Number(rows[0]?.n ?? 0);
    }
    return total;
}
/**
 * Переганяє все, що лишилося. Безпечно викликати повторно й безпечно
 * переривати: див. пояснення про облік прогресу вище.
 */
export async function reencryptContent(db, cipher, onProgress) {
    const total = await countPending(db);
    let done = 0;
    onProgress?.({ done, total });
    for (const { table, column } of TARGETS) {
        // Без OFFSET: перешифровані рядки перестають підпадати під умову,
        // тож наступна вибірка сама починається там, де скінчилася попередня.
        // З OFFSET ми б пропускали рядки на кожному кроці.
        for (;;) {
            const rows = await db.query(`SELECT id, ${column} AS blob FROM ${table} WHERE ${stillOld(column)} LIMIT ${BATCH}`, [OLD_HEADER]);
            if (rows.length === 0)
                break;
            for (const row of rows) {
                const plain = await cipher.open(row.blob, row.id);
                const sealed = await cipher.seal(plain, row.id);
                await db.exec(`UPDATE ${table} SET ${column} = ? WHERE id = ?`, [sealed, row.id]);
                done += 1;
            }
            onProgress?.({ done, total });
        }
    }
    return done;
}
//# sourceMappingURL=reencrypt.js.map